import React, { ReactNode } from 'react';
import { Alert, Button } from '@mui/material';
import { useGetEmployeeById } from '../../services/react-query/employees';
import { AttendanceCommand, AttendanceStatus, Event } from '../../types/Event';
import { _nullishCoalesce } from '@sentry/core';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useUpdateEventAttendance } from '../../services/react-query/events';
import { DateTime } from 'luxon';
import { TaskStatus } from '../../types/Task';
import { taskBaseColor } from "../../utility/task";

interface IAttendanceBannerProps {
  event: Event;
}

export default function AttendanceBanner({ event }: IAttendanceBannerProps) {
  const { data: currentEmployeeData } = useGetEmployeeById({ uuid: 'current' });
  const { uuid } = useParams();
  const { mutate: updateAttendance } = useUpdateEventAttendance({ uuid: uuid ?? '' });

  if (!currentEmployeeData || !uuid) return null;

  const now = DateTime.now();
  const eventStart = DateTime.fromISO(event.start_at);
  const attendanceCutoff = eventStart.minus({ hours: 12 });
  const checkInCutoff = eventStart.minus({ hours: 1 });

  const ownEes = _.filter(event.event_employees, { employee: { uuid: currentEmployeeData.uuid } });

  const allowAccept = now <= attendanceCutoff && _.includes(
    _.map(ownEes, 'status'),
    AttendanceStatus.PENDING
  );
  const checkedOut = _.some(ownEes, 'checked_out_at');
  const allowCheckin = now >= checkInCutoff && now < DateTime.fromISO(event.end_at);
  const allowCheckout = _.some(ownEes, 'checked_in_at') && !checkedOut;


  let text: ReactNode = null;
  let action: ReactNode = null;
  if (allowAccept) {
    text = 'You have a pending assignment to this event. Please accept or decline.';
    action = (
      <>
        <Button color="success" onClick={() => updateAttendance(AttendanceCommand.ACCEPT)}>
          Accept
        </Button>
        <Button color="error" onClick={() => updateAttendance(AttendanceCommand.DECLINE)}>
          Decline
        </Button>
      </>
    );
  } else if (allowCheckout) {
    text = 'You are checked in. You can click here to check out.';
    action = (
      <Button onClick={() => updateAttendance(AttendanceCommand.CHECK_OUT)} sx={{ color: taskBaseColor(TaskStatus.IN_PROGRESS) }}>
        Check Out
      </Button>
    );
  } else if (allowCheckin) {
    text = checkedOut ?
      'You are checked out. You can check in again by clicking here.' :
      'The event has started. If you\'ve arrived please click here to check in.';
    action = (
      <Button color="primary" onClick={() => updateAttendance(AttendanceCommand.CHECK_IN)}>
        Check In
      </Button>
    );
  } else {
    return null;
  }

  return (
    <Alert severity="info" action={action}>
      {text}
    </Alert>
  );
}
